import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { makeApiCall } from "../../helpers/postRequest";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import {
  IPastPaymentStatusCount,
  IPaymentDetails,
  IPaymentList,
  IPaymentOverview,
  IPaymentStatusCount,
} from "./paymentDetails.types";
import moment from "moment";

const getMonthStartEndDate = (month: string, year: string) => {
  const formattedMonth = month.padStart(2, "0");

  const start = moment(`${year}-${formattedMonth}-01`)
    .startOf("month")
    .format("YYYY-MM-DD");
  const end = moment(`${year}-${formattedMonth}-01`)
    .endOf("month")
    .format("YYYY-MM-DD");

  return { start, end };
};

// Action Types
export const FETCH_PAYMENT_DETAILS_LIST_PROGRESS =
  "FETCH_PAYMENT_DETAILS_LIST_PROGRESS";
export const FETCH_PAYMENT_DETAILS_LIST_SUCCESS =
  "FETCH_PAYMENT_DETAILS_LIST_SUCCESS";
export const FETCH_PAYMENT_DETAILS_LIST_FAILED =
  "FETCH_PAYMENT_DETAILS_LIST_FAILED";

export const FETCH_PAYMENT_DETAIL_PROGRESS = "FETCH_PAYMENT_DETAIL_PROGRESS";
export const FETCH_PAYMENT_DETAIL_SUCCESS = "FETCH_PAYMENT_DETAIL_SUCCESS";
export const FETCH_PAYMENT_DETAIL_FAILED = "FETCH_PAYMENT_DETAIL_FAILED";

export const CLEAR_PAYMENT_DETAIL = "CLEAR_PAYMENT_DETAIL";
export const CLEAR_PAYMENT_DETAIL_STATE = "CLEAR_PAYMENT_DETAIL_STATE";

// Action Creators
export const fetchPaymentDetailsListProgress = () =>
  action(FETCH_PAYMENT_DETAILS_LIST_PROGRESS);
export const fetchPaymentDetailsListSuccess = (
  Payment_list: any[],
  totalRecords: number
) => action(FETCH_PAYMENT_DETAILS_LIST_SUCCESS, { Payment_list, totalRecords });
export const fetchPaymentDetailsListFailed = (errorMessage: string) =>
  action(FETCH_PAYMENT_DETAILS_LIST_FAILED, { errorMessage });

export const fetchPaymentDetailProgress = () =>
  action(FETCH_PAYMENT_DETAIL_PROGRESS);
export const fetchPaymentDetailSuccess = (paymentDetail: any) =>
  action(FETCH_PAYMENT_DETAIL_SUCCESS, { paymentDetail });
export const fetchPaymentDetailFailed = (errorMessage: string) =>
  action(FETCH_PAYMENT_DETAIL_FAILED, { errorMessage });

export const clearPaymentDetailState = () => action(CLEAR_PAYMENT_DETAIL_STATE);
export const clearPaymentDetail = () => action(CLEAR_PAYMENT_DETAIL);

// Async Actions
export const fetchPaymentDetailsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    searchValue: IDatatableV2AdvancedSearchFilter,
    companyOutletUuid: string,
    searchByNameOrNumber: string,
    searchByVenue: string,
    otherCustomStateProperties: { [key: string]: any }
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const {
        searchByEventType,
        functionDate: { month, year },
        followupDate,
      } = otherCustomStateProperties;

      dispatch(fetchPaymentDetailsListProgress());

      let url = `/accounting/get-payment-dashboard?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&pageLimit=${limit}`;

      if (
        searchValue.length > 0 ||
        searchByNameOrNumber ||
        searchByVenue ||
        month ||
        year ||
        followupDate ||
        searchByEventType
      ) {
        const advanceFilter = [...searchValue];

        if (searchByNameOrNumber) {
          advanceFilter.push({
            column: ["name_of_guest", "contact_number_one"],
            value: searchByNameOrNumber,
            logicalOperator: "OR",
            operator: "CONTAINS",
          });
        }

        if (searchByVenue) {
          advanceFilter.push({
            column: ["preferred_venue"],
            value: searchByVenue,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && !year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `-${month}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (!month && year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `${year}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && year) {
          const { start, end } = getMonthStartEndDate(month, year);
          advanceFilter.push({
            column: ["function_date"],
            value: start,
            logicalOperator: "AND",
            operator: "GREATER_THAN_EQUAL",
          });
          advanceFilter.push({
            column: ["function_date"],
            value: end,
            logicalOperator: "AND",
            operator: "LESSER_THAN_EQUAL",
          });
        }

        if (followupDate) {
          advanceFilter.push({
            column: ["function_date"],
            value: followupDate,
            logicalOperator: "AND",
            operator: "EQUAL",
          });
        }

        if (searchByEventType && searchByEventType.length > 0) {
          advanceFilter.push({
            column: ["event_type"],
            value: searchByEventType,
            logicalOperator: "AND",
            operator: "IN",
          });
        }
        url += `&advanceFilter=${encodeURIComponent(JSON.stringify(advanceFilter))}`;
      }

      if (companyOutletUuid) {
        url += `&company_outlet_uuid=${companyOutletUuid}`;
      }

      const res = await api.get(url);
      const data = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchPaymentDetailsListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchPaymentDetailsListFailed(err.message));
    }
  };

export const fetchPaymentDetailAsync =
  (paymentId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchPaymentDetailProgress());
      dispatch(saveLoaderProgress());

      const res = await api.get(
        `/accounting/get-payment-transaction/${paymentId}`
      );
      if (!res.data.data) throw new Error("No payment data found");

      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchPaymentDetailSuccess(data[0]));
      } else {
        dispatch(
          fetchPaymentDetailFailed(
            "Unfortunately, there are no payment details available at the moment."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchPaymentDetailFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "An error occurred while fetching payment detail",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertPaymentDetailAsync =
  (
    paymentDetails: IPaymentDetails,
    onCallBack: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      // Destructure properly and use the cleaned object
      const {
        create_ts,
        insert_ts,
        modified_by_name,
        modified_by_uuid,
        created_by_name,
        created_by_uuid,
        ...cleanDetails
      } = paymentDetails;

      const res = await api.post(
        "/accounting/upsert-payment-transaction",
        cleanDetails // Use the cleaned object instead of original
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Payment details saved successfully",
          displayAs: "snackbar",
        })
      );

      onCallBack(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong!",
          displayAs: "snackbar",
        })
      );
      onCallBack(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_LEAD_PAYMENT_DETAILS_LIST_PROGRESS =
  "FETCH_LEAD_PAYMENT_DETAILS_LIST_PROGRESS";
export const FETCH_LEAD_PAYMENT_DETAILS_LIST_SUCCESS =
  "FETCH_LEAD_PAYMENT_DETAILS_LIST_SUCCESS";
export const FETCH_LEAD_PAYMENT_DETAILS_LIST_FAILED =
  "FETCH_LEAD_PAYMENT_DETAILS_LIST_FAILED";

export const fetchLeadPaymentDetailsListProgress = () =>
  action(FETCH_LEAD_PAYMENT_DETAILS_LIST_PROGRESS);
export const fetchLeadPaymentDetailsListSuccess = (list: any[]) =>
  action(FETCH_LEAD_PAYMENT_DETAILS_LIST_SUCCESS, { list });
export const fetchLeadPaymentDetailsListFailed = (errorMessage: string) =>
  action(FETCH_LEAD_PAYMENT_DETAILS_LIST_FAILED, { errorMessage });

export const fetchLeadPaymentDetailsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    searchValue: IDatatableV2AdvancedSearchFilter,
    companyOutletUuid: string,
    leadUUID: string,
    multipleEventsUUID?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchLeadPaymentDetailsListProgress());

      let url = `/accounting/get-payment-transaction?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&pageLimit=${limit}`;

      if (leadUUID) {
        url += `&leads_uuid=${leadUUID}`;
      }

      if (multipleEventsUUID) {
        url += `&multiple_events_uuid=${multipleEventsUUID}`;
      }

      if (companyOutletUuid) {
        url += `&company_outlet_uuid=${companyOutletUuid}`;
      }

      if (searchValue.length > 0) {
        url += `&advanceFilter=${encodeURIComponent(JSON.stringify(searchValue))}`;
      }
      const res = await api.get(url);
      const data = res.data.data;

      dispatch(fetchLeadPaymentDetailsListSuccess(data));
    } catch (err: any) {
      dispatch(fetchLeadPaymentDetailsListFailed(err.message));
    }
  };

export const fetchPaymentPreviewAsync =
  (
    paymentId: string,
    type: string,
    onCallback: (isSuccess: boolean, html?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.get(
        `/accounting/get-transaction-receipt-pdf-or-preview/${paymentId}/${type}`
      );
      onCallback(true, res.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch payment preview",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const fetchPaymentDownloadPdfAsync =
  (
    paymentId: string,
    type: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/accounting/get-transaction-receipt-pdf-or-preview/${paymentId}/${type}`,
        {
          responseType: "arraybuffer",
          validateStatus: (status) => status === 200,
        }
      );

      const contentType = res.headers["content-type"];
      if (!contentType.includes("application/pdf")) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "payment-details.pdf";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      onCallback(true);
    } catch (err: any) {
      let errorMessage = "Failed to download PDF";
      if (err.response?.data) {
        const decoder = new TextDecoder("utf-8");
        const errorData = decoder.decode(err.response.data);
        try {
          errorMessage = JSON.parse(errorData).message || errorMessage;
        } catch {
          errorMessage = errorData;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_PAYMENT_STATUS_COUNT_PROGRESS =
  "FETCH_PAYMENT_STATUS_COUNT_PROGRESS";
export const FETCH_PAYMENT_STATUS_COUNT_SUCCESS =
  "FETCH_PAYMENT_STATUS_COUNT_SUCCESS";
export const FETCH_PAYMENT_STATUS_COUNT_FAILED =
  "FETCH_PAYMENT_STATUS_COUNT_FAILED";

export const fetchPaymentStatusCountProgress = () =>
  action(FETCH_PAYMENT_STATUS_COUNT_PROGRESS);
export const fetchPaymentStatusCountSuccess = (data: IPaymentStatusCount[]) =>
  action(FETCH_PAYMENT_STATUS_COUNT_SUCCESS, { data });
export const fetchPaymentStatusCountFailed = () =>
  action(FETCH_PAYMENT_STATUS_COUNT_FAILED);

export const fetchPaymentStatusCountAsync =
  (companyOutlet?: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPaymentStatusCountProgress());
      let url = "";

      if (companyOutlet) {
        url += `&company_outlet_uuid=${companyOutlet}`;
      }

      const finalUrl =
        `/dashboard/get-payment-overview?type=count&sub_type=OUTSTANDING_BTC` +
        url;

      const res = await api.get(finalUrl);

      const data: IPaymentStatusCount[] = res.data.data;

      dispatch(fetchPaymentStatusCountSuccess(data || []));
    } catch (err: any) {
      dispatch(fetchPaymentStatusCountFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_PAST_PAYMENT_STATUS_COUNT_PROGRESS =
  "FETCH_PAST_PAYMENT_STATUS_COUNT_PROGRESS";
export const FETCH_PAST_PAYMENT_STATUS_COUNT_SUCCESS =
  "FETCH_PAST_PAYMENT_STATUS_COUNT_SUCCESS";
export const FETCH_PAST_PAYMENT_STATUS_COUNT_FAILED =
  "FETCH_PAST_PAYMENT_STATUS_COUNT_FAILED";

export const fetchPastPaymentStatusCountProgress = () =>
  action(FETCH_PAST_PAYMENT_STATUS_COUNT_PROGRESS);
export const fetchPastPaymentStatusCountSuccess = (
  data: IPastPaymentStatusCount[]
) => action(FETCH_PAST_PAYMENT_STATUS_COUNT_SUCCESS, { data });
export const fetchPastPaymentStatusCountFailed = () =>
  action(FETCH_PAST_PAYMENT_STATUS_COUNT_FAILED);

export const fetchPastPaymentStatusCountAsync =
  (companyOutlet?: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPastPaymentStatusCountProgress());
      let url = "";
      if (companyOutlet) {
        url += `?company_outlet_uuid=${companyOutlet}`;
      }
      const finalUrl = `/dashboard/get-past-payment-overview` + url;
      const res = await api.get(finalUrl);
      const data: IPastPaymentStatusCount[] = res.data.data;
      dispatch(fetchPastPaymentStatusCountSuccess(data || []));
    } catch (err: any) {
      dispatch(fetchPastPaymentStatusCountFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "PastPayment Number not found",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_PAYMENT_LIST_STATUS_COUNT_PROGRESS =
  "FETCH_PAYMENT_LIST_STATUS_COUNT_PROGRESS";
export const FETCH_PAYMENT_LIST_STATUS_COUNT_SUCCESS =
  "FETCH_PAYMENT_LIST_STATUS_COUNT_SUCCESS";
export const FETCH_PAYMENT_LIST_STATUS_COUNT_FAILED =
  "FETCH_PAYMENT_LIST_STATUS_COUNT_FAILED";

export const fetchPaymentListStatusCountProgress = () =>
  action(FETCH_PAYMENT_LIST_STATUS_COUNT_PROGRESS);
export const fetchPaymentListStatusCountSuccess = (
  list: IPaymentList[],
  totalRecords: number
) => action(FETCH_PAYMENT_LIST_STATUS_COUNT_SUCCESS, { list, totalRecords });
export const fetchPaymentListStatusCountFailed = () =>
  action(FETCH_PAYMENT_LIST_STATUS_COUNT_FAILED);

export const fetchLPaymentListStatusCountAsync =
  (
    type: string,
    status: string,
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    searchValue: IDatatableV2AdvancedSearchFilter,
    company_outlet_uuid: string,
    searchByName: string,
    searchByNo: string,
    searchByVenue: string,
    otherCustomStateProperties: { [key: string]: any }
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        searchByEventType,
        functionDate: { month, year },
      } = otherCustomStateProperties;

      dispatch(fetchPaymentListStatusCountProgress());

      let url = `/dashboard/get-payment-overview?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

      if (
        searchValue.length > 0 ||
        searchByName ||
        searchByVenue ||
        month ||
        year ||
        searchByNo ||
        searchByEventType
      ) {
        const advanceFilter = [...searchValue];

        if (searchByName) {
          advanceFilter.push({
            column: ["name_of_guest"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (searchByVenue) {
          advanceFilter.push({
            column: ["preferred_venue"],
            value: searchByVenue,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && !year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `-${month}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (!month && year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `${year}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && year) {
          const { start, end } = getMonthStartEndDate(month, year);
          advanceFilter.push({
            column: ["function_date"],
            value: start,
            logicalOperator: "AND",
            operator: "GREATER_THAN_EQUAL",
          });
          advanceFilter.push({
            column: ["function_date"],
            value: end,
            logicalOperator: "AND",
            operator: "LESSER_THAN_EQUAL",
          });
        }

        if (searchByNo) {
          advanceFilter.push({
            column: ["contact_number_one"],
            value: searchByNo,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (searchByEventType && searchByEventType.length > 0) {
          advanceFilter.push({
            column: ["event_type"],
            value: searchByEventType,
            logicalOperator: "AND",
            operator: "IN",
          });
        }
        url += `&advanceFilter=${encodeURIComponent(JSON.stringify(advanceFilter))}`;
      }
      if (type) {
        url += `&type=${type}`;
      }
      if (status !== "-1") {
        url += `&sub_type=${status}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      const res = await api.get(url);
      const data: IPaymentList[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchPaymentListStatusCountSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchPaymentListStatusCountFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
